import React, { useCallback, useEffect, useMemo, useState } from "react";

import Sidebar from "../../components/Sidebar";
import Button from "../../components/Buttons/Button";
import Switch from "../../components/Switch";
import { successIcon as SuccessIcon } from "assets/icons";
import { useNotify } from "context/NotificationContext";
import {
  cancelSubscription,
  createPayment,
  fetchActiveSubscription,
  fetchTariffFunctionMatrix,
  fetchTariffs,
  setAutoRenewal,
} from "../../services/subscriptionService/subscriptionService";
import {
  SubscriptionResponse,
  Tariff,
  TariffFunctionMatrix,
} from "../../services/subscriptionService/subscriptionService.types";
import {
  daysRemaining,
  formatDate,
  formatMoney,
  orderTariffs,
  periodLabel,
  visibleTariffFunctions,
} from "./profile.utils";
import {
  AgreementText,
  ContentInner,
  ContentWrapper,
  EmptyHeaderCell,
  ErrorState,
  FeatureCell,
  FeatureNameCell,
  FeatureNo,
  FeatureRow,
  FeatureYes,
  FootNote,
  LoadingState,
  ModalActions,
  ModalActionsSpacer,
  ModalCard,
  ModalOverlay,
  ModalSetting,
  ModalSummary,
  ModalText,
  ModalTitle,
  PlanAction,
  PlanBadge,
  PlanBadgeUntil,
  PlanDescription,
  PlanHeader,
  PlanName,
  PlanPrice,
  PlanStorage,
  PlanStatusSpace,
  PricingScroll,
  PricingTable,
  ProfilePageContainer,
  SectionTitle,
  SettingName,
  SettingNote,
  TableHeaderBar,
  Title,
} from "./ProfilePage.styles";

const LICENSE_URL = "/Лицензионное соглашение на использование платформы «ЛИСРЕНТ».docx";
const USER_AGREEMENT_URL = "/Пользовательское соглашение платформы LISRENT.docx";

type BusyAction = "autoRenewal" | "cancel" | "renew" | "change" | null;

const errorDetail = (error: unknown, fallback: string): string => {
  const responseError = error as { response?: { data?: { error?: string } } };
  return responseError.response?.data?.error || fallback;
};

const ProfilePage: React.FC = () => {
  const notify = useNotify();
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [matrix, setMatrix] = useState<TariffFunctionMatrix | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<Tariff | null>(null);
  const [busyAction, setBusyAction] = useState<BusyAction>(null);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [tariffsData, matrixData] = await Promise.all([
        fetchTariffs(),
        fetchTariffFunctionMatrix(),
      ]);
      setTariffs(tariffsData);
      setMatrix(matrixData);

      const current = tariffsData.find((tariff) => tariff.is_active);
      setSubscription(current ? await fetchActiveSubscription(current.id) : null);
    } catch (error) {
      setLoadError(true);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!manageOpen && !selectedTariff && !cancelConfirmOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || busyAction) return;
      if (cancelConfirmOpen) {
        setCancelConfirmOpen(false);
        return;
      }
      setManageOpen(false);
      setSelectedTariff(null);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [busyAction, cancelConfirmOpen, manageOpen, selectedTariff]);

  const currentTariff = useMemo(
    () => tariffs.find((tariff) => tariff.is_active) || null,
    [tariffs],
  );
  const orderedTariffs = useMemo(() => orderTariffs(tariffs), [tariffs]);
  const visibleFunctions = useMemo(
    () => (matrix ? visibleTariffFunctions(matrix, orderedTariffs, false) : []),
    [matrix, orderedTariffs],
  );

  const handleAutoRenewal = async (nextValue: boolean) => {
    if (!currentTariff || !subscription || busyAction) return;
    setBusyAction("autoRenewal");
    try {
      const response = await setAutoRenewal(currentTariff.id, nextValue);
      setSubscription((current) =>
        current ? { ...current, auto_renewal: response.auto_renewal } : current,
      );
      notify.show({
        severity: "success",
        summary: "Подписка",
        detail: response.message,
        life: 3000,
      });
    } catch (error) {
      notify.show({
        severity: "error",
        summary: "Подписка",
        detail: errorDetail(error, "Не удалось изменить автопродление."),
        life: 5000,
      });
    } finally {
      setBusyAction(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!currentTariff || !subscription || busyAction) return;
    setBusyAction("cancel");
    try {
      const response = await cancelSubscription(currentTariff.id);
      setSubscription((current) =>
        current ? { ...current, auto_renewal: false } : current,
      );
      setCancelConfirmOpen(false);
      setManageOpen(false);
      notify.show({
        severity: "success",
        summary: "Подписка",
        detail: response.message,
        life: 4500,
      });
    } catch (error) {
      notify.show({
        severity: "error",
        summary: "Подписка",
        detail: errorDetail(error, "Не удалось отменить продление подписки."),
        life: 5000,
      });
    } finally {
      setBusyAction(null);
    }
  };

  const handleRenewNow = async () => {
    if (!currentTariff || busyAction) return;
    setBusyAction("renew");
    try {
      const paymentUrl = await createPayment(currentTariff.id, "renew");
      window.location.href = paymentUrl;
    } catch (error) {
      setBusyAction(null);
      notify.show({
        severity: "error",
        summary: "Оплата",
        detail: errorDetail(error, "Не удалось перейти к продлению."),
        life: 5000,
      });
    }
  };

  const handleChangeTariff = async () => {
    if (!selectedTariff || busyAction) return;
    setBusyAction("change");
    try {
      const paymentUrl = await createPayment(selectedTariff.id, "change");
      window.location.href = paymentUrl;
    } catch (error) {
      setBusyAction(null);
      notify.show({
        severity: "error",
        summary: "Оплата",
        detail: errorDetail(error, "Не удалось перейти к оплате нового тарифа."),
        life: 5000,
      });
    }
  };

  const currentEndDate = subscription
    ? formatDate(subscription.end_date)
    : currentTariff?.expiration_date || "";
  const currentDaysLeft = subscription ? daysRemaining(subscription.end_date) : 0;

  return (
    <ProfilePageContainer>
      <Sidebar />
      <ContentWrapper>
        <ContentInner>
          <Title>Личный кабинет</Title>

          {loading && <LoadingState>Загрузка тарифов и подписки...</LoadingState>}

          {!loading && loadError && (
            <ErrorState>
              Не удалось загрузить личный кабинет.
              <Button variant="secondary" onClick={() => void loadProfile()}>
                Повторить
              </Button>
            </ErrorState>
          )}

          {!loading && !loadError && matrix && (
            <>
              <TableHeaderBar>
                <SectionTitle>Тарифы</SectionTitle>
              </TableHeaderBar>

              <PricingScroll>
                <PricingTable>
                  <thead>
                    <tr>
                      <EmptyHeaderCell />
                      {orderedTariffs.map((tariff) => {
                        const isCurrent = currentTariff?.id === tariff.id;
                        return (
                          <PlanHeader key={tariff.id} $current={isCurrent}>
                            <PlanStatusSpace>
                              {isCurrent && (
                                <PlanBadge>
                                  <span>Активна</span>
                                  <PlanBadgeUntil $warning={currentDaysLeft <= 5}>
                                    до {currentEndDate}
                                  </PlanBadgeUntil>
                                </PlanBadge>
                              )}
                            </PlanStatusSpace>
                            <PlanName>{tariff.name}</PlanName>
                            <PlanPrice>
                              {tariff.by_request ? (
                                "По запросу"
                              ) : (
                                <>
                                  {formatMoney(tariff.price)}{" "}
                                  <span>/ {periodLabel(tariff.duration_days)}</span>
                                </>
                              )}
                            </PlanPrice>
                            <PlanStorage>
                              <strong>{tariff.storage_limit_gb} ГБ</strong>
                              <span>для курсов, файлов и ответов учеников</span>
                            </PlanStorage>
                            <PlanDescription>{tariff.description}</PlanDescription>
                            <PlanAction
                              title={
                                tariff.by_request
                                  ? "Отправка заявок для этого тарифа пока не подключена"
                                  : undefined
                              }
                            >
                              {isCurrent ? (
                                <Button
                                  variant="secondary"
                                  disabled={!subscription}
                                  onClick={() => setManageOpen(true)}
                                >
                                  Управлять подпиской
                                </Button>
                              ) : tariff.by_request ? (
                                <Button variant="secondary" disabled>
                                  Оставить заявку
                                </Button>
                              ) : (
                                <Button variant="primary" onClick={() => setSelectedTariff(tariff)}>
                                  Перейти
                                </Button>
                              )}
                            </PlanAction>
                          </PlanHeader>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleFunctions.map((feature, rowIndex) => {
                      const isLastRow = rowIndex === visibleFunctions.length - 1;
                      return (
                        <FeatureRow key={feature.id}>
                          <FeatureNameCell>{feature.name}</FeatureNameCell>
                          {feature.flags.map((hasFeature, index) => {
                            const tariff = orderedTariffs[index];
                            const isCurrent = currentTariff?.id === tariff?.id;
                            return (
                              <FeatureCell
                                key={tariff?.id ?? index}
                                $current={isCurrent}
                                $lastRow={isLastRow}
                              >
                                {hasFeature ? (
                                  <FeatureYes>
                                    <SuccessIcon aria-hidden="true" />
                                  </FeatureYes>
                                ) : (
                                  <FeatureNo>—</FeatureNo>
                                )}
                              </FeatureCell>
                            );
                          })}
                        </FeatureRow>
                      );
                    })}
                  </tbody>
                </PricingTable>
              </PricingScroll>

              <FootNote>
                Оплата проходит через ЮKassa. При смене тарифа новая подписка начинается с даты
                оплаты, оставшиеся дни текущего тарифа не переносятся.
              </FootNote>
            </>
          )}
        </ContentInner>
      </ContentWrapper>

      {manageOpen && currentTariff && subscription && (
        <ModalOverlay
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !busyAction) setManageOpen(false);
          }}
        >
          <ModalCard role="dialog" aria-modal="true" aria-labelledby="manage-subscription-title">
            {!cancelConfirmOpen ? (
              <>
                <ModalTitle id="manage-subscription-title">Управление подпиской</ModalTitle>
                <ModalSummary>
                  <div><span>Тариф</span><strong>{currentTariff.name}</strong></div>
                  <div>
                    <span>Стоимость</span>
                    <strong>{formatMoney(currentTariff.price)} / {periodLabel(currentTariff.duration_days)}</strong>
                  </div>
                  <div><span>Начало</span><strong>{formatDate(subscription.start_date)}</strong></div>
                  <div><span>Действует до</span><strong>{formatDate(subscription.end_date)}</strong></div>
                  <div><span>Осталось</span><strong>{currentDaysLeft} дн.</strong></div>
                </ModalSummary>

                <ModalSetting>
                  <div>
                    <SettingName>Автопродление</SettingName>
                    <SettingNote>
                      {subscription.auto_renewal
                        ? `Следующее списание — ${formatDate(subscription.end_date)}`
                        : `Доступ закончится ${formatDate(subscription.end_date)}`}
                    </SettingNote>
                  </div>
                  <Switch
                    checked={subscription.auto_renewal}
                    disabled={busyAction === "autoRenewal"}
                    ariaLabel="Автопродление"
                    onChange={(checked) => void handleAutoRenewal(checked)}
                  />
                </ModalSetting>

                <ModalActions>
                  <Button
                    variant="dangerSecondary"
                    disabled={Boolean(busyAction)}
                    onClick={() => setCancelConfirmOpen(true)}
                  >
                    Отменить подписку
                  </Button>
                  <ModalActionsSpacer />
                  <Button variant="secondary" disabled={Boolean(busyAction)} onClick={() => setManageOpen(false)}>
                    Закрыть
                  </Button>
                  <Button variant="primary" loading={busyAction === "renew"} disabled={Boolean(busyAction && busyAction !== "renew")} onClick={() => void handleRenewNow()}>
                    Продлить сейчас
                  </Button>
                </ModalActions>
              </>
            ) : (
              <>
                <ModalTitle id="manage-subscription-title">Отменить подписку</ModalTitle>
                <ModalText>
                  Тариф «{currentTariff.name}» продолжит работать до {formatDate(subscription.end_date)}.
                  После этой даты доступ закончится, автопродление выполняться не будет.
                </ModalText>
                <ModalActions>
                  <Button variant="secondary" disabled={Boolean(busyAction)} onClick={() => setCancelConfirmOpen(false)}>
                    Назад
                  </Button>
                  <Button variant="danger" loading={busyAction === "cancel"} disabled={Boolean(busyAction && busyAction !== "cancel")} onClick={() => void handleCancelSubscription()}>
                    Отменить подписку
                  </Button>
                </ModalActions>
              </>
            )}
          </ModalCard>
        </ModalOverlay>
      )}

      {selectedTariff && (
        <ModalOverlay
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !busyAction) setSelectedTariff(null);
          }}
        >
          <ModalCard role="dialog" aria-modal="true" aria-labelledby="change-tariff-title">
            <ModalTitle id="change-tariff-title">Смена тарифа</ModalTitle>
            <ModalText>
              {currentTariff
                ? "Подписка начнётся заново с даты оплаты, оставшиеся дни текущего тарифа не переносятся."
                : "Подписка начнётся с даты успешной оплаты."}
            </ModalText>
            <ModalSummary>
              <div><span>Сейчас</span><strong>{currentTariff?.name || "Нет активного тарифа"}</strong></div>
              <div><span>Новый тариф</span><strong>{selectedTariff.name}</strong></div>
              <div><span>К оплате</span><strong>{formatMoney(selectedTariff.price)}</strong></div>
            </ModalSummary>
            <AgreementText>
              Нажимая «Перейти к оплате», вы принимаете{" "}
              <a href={LICENSE_URL} target="_blank" rel="noopener noreferrer">лицензионное соглашение</a>{" "}
              и{" "}
              <a href={USER_AGREEMENT_URL} target="_blank" rel="noopener noreferrer">пользовательское соглашение</a>.
            </AgreementText>
            <ModalActions>
              <Button variant="secondary" disabled={Boolean(busyAction)} onClick={() => setSelectedTariff(null)}>
                Отмена
              </Button>
              <Button variant="primary" loading={busyAction === "change"} disabled={Boolean(busyAction && busyAction !== "change")} onClick={() => void handleChangeTariff()}>
                Перейти к оплате
              </Button>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}
    </ProfilePageContainer>
  );
};

export default ProfilePage;
